/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ISummaryDetailMethods extends IPluginMethodMap {
  getSummaryDetailProps(args: any): Promise<any>;
}

export class SummaryDetailService implements IAgentPlugin {
  readonly methods: ISummaryDetailMethods;

  constructor() {
    this.methods = {
      getSummaryDetailProps: this.getSummaryDetailProps.bind(this),
    };
  }

  async getSummaryDetailProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    if (!client) {
      return {
        defaultData: [],
        specialReserveData: [],
        sectionSpecificData: [],
      };
    }

    const baseFilter: any = {};
    if (args?.criteria?.presentationId) {
      baseFilter.presentationId = args.criteria.presentationId;
    }

    // Do not trigger query until valid presentationId is present
    if (!baseFilter.presentationId) {
      return {
        defaultData: [],
        specialReserveData: [],
        sectionSpecificData: [],
      };
    }

    const nameFilter = {
      contains: args?.criteria?.name?.contains || "",
    };

    // Retrieve a larger size (100) by default to support full client-side paging/sorting
    const options = {
      limit: args?.size ?? 100,
      skip: args?.offset ?? 0,
    };

    try {
      const [defaultRes, specialRes, sectionRes] = await Promise.all([
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              presentationId: baseFilter.presentationId,
              partition: "default",
              name: nameFilter,
            },
            options,
          },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              presentationId: baseFilter.presentationId,
              partition: "special",
              name: nameFilter,
            },
            options,
          },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_COSTUME_TYPE_PARTITION_INVENTORY_REPORT,
          variables: {
            filter: {
              presentationId: baseFilter.presentationId,
              partition: "specific",
              name: nameFilter,
            },
            options,
          },
          fetchPolicy: "network-only",
        }),
      ]);

      const formatResponse = (res: any) => {
        const content = res?.data?.getCostumeTypePartitionInventoryReport?.content || [];
        return content.map((item: any) => ({
          id: item.id || item.costumeType,
          costumeOption: item.costumeType,
          inventory: item.inventory ?? 0,
          assigned: item.assigned ?? 0,
          remaining: item.remaining ?? 0,
        }));
      };

      const defaultData = formatResponse(defaultRes);
      const specialReserveData = formatResponse(specialRes);
      const sectionSpecificData = formatResponse(sectionRes);

      return {
        defaultData,
        specialReserveData,
        sectionSpecificData,
        count: defaultRes?.data?.getCostumeTypePartitionInventoryReport?.count ?? defaultData.length,
        limit: defaultRes?.data?.getCostumeTypePartitionInventoryReport?.limit ?? 100,
        skip: defaultRes?.data?.getCostumeTypePartitionInventoryReport?.skip ?? 0,
      };
    } catch (err: any) {
      console.log(`error in getSummaryDetailProps`, err);
      return {
        defaultData: [],
        specialReserveData: [],
        sectionSpecificData: [],
      };
    }
  }
}
